import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Card, CardBody, SimpleGrid, Badge, HStack, VStack, Text, Heading, Tag,
  Button, IconButton, useDisclosure, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, Input, Select, Switch, Textarea,
  Divider, Box,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import PageShell from '../components/PageShell';
import ConfirmModal from '../components/ConfirmModal';
import packageService from '../services/packageService';
import i18n from '../i18n';

const PACKAGE_TYPES = [
  { value: 'meal', labelKey: 'packages.packageTypes.meal' },
  { value: 'spa', labelKey: 'packages.packageTypes.spa' },
  { value: 'business', labelKey: 'packages.packageTypes.business' },
  { value: 'corporate', labelKey: 'packages.packageTypes.corporate' },
  { value: 'romance', labelKey: 'packages.packageTypes.romance' },
  { value: 'family', labelKey: 'packages.packageTypes.family' },
  { value: 'themed', labelKey: 'packages.packageTypes.themed' },
  { value: 'extended_stay', labelKey: 'packages.packageTypes.extended_stay' },
  { value: 'seasonal', labelKey: 'packages.packageTypes.seasonal' },
  { value: 'other', labelKey: 'packages.packageTypes.other' },
];

const SERVICE_TYPES = [
  { value: 'food', labelKey: 'packages.serviceTypes.food' },
  { value: 'spa', labelKey: 'packages.serviceTypes.spa' },
  { value: 'transport', labelKey: 'packages.serviceTypes.transport' },
  { value: 'activity', labelKey: 'packages.serviceTypes.activity' },
  { value: 'amenity', labelKey: 'packages.serviceTypes.amenity' },
  { value: 'service', labelKey: 'packages.serviceTypes.service' },
  { value: 'other', labelKey: 'packages.serviceTypes.other' },
];

const TYPE_COLORS = {
  meal: 'orange', spa: 'pink', business: 'blue', corporate: 'cyan',
  romance: 'red', family: 'green', themed: 'purple', extended_stay: 'teal',
  seasonal: 'yellow', other: 'gray',
};

const emptyForm = {
  name: '', code: '', package_type: 'other', description: '',
  base_rate_plan: '', package_rate_adjustment: '0.00', adjustment_type: 'fixed',
  los_discount_min_nights: '', los_discount_type: 'fixed', los_discount_value: '0.00',
  start_date: '', end_date: '', is_active: true, show_in_booking_engine: false,
  requires_advance_booking: false, advance_booking_days: '', promo_code: '',
  services: [],
};

const emptyService = {
  service_name: '', service_type: 'food', service_cost: '0.00',
  is_daily: true, is_per_person: false,
};

// "5.00 / person / night", "25.00 / person / stay", "10.00 / room / night"
// or "Included" — how a service row actually bills (backend
// rates.packages.price_terms: x nights when is_daily, x guests when
// is_per_person, otherwise x rooms).
const servicePriceLabel = (s) => {
  const cost = Number(s.service_cost || 0);
  if (!cost) return i18n.t('packages.servicePrice.included');
  const unit = `${s.is_per_person ? 'person' : 'room'}${s.is_daily ? 'Night' : 'Stay'}`;
  return i18n.t(`packages.servicePrice.${unit}`, { cost: cost.toFixed(2) });
};

// Same arithmetic as rates.packages.price_terms, for the live example in
// the editor. Percentage adjustments depend on the room price, so they are
// described rather than summed.
const examplePackageTotal = (form, { nights, guests, rooms }) => {
  let total = 0;
  const adjustment = Number(form.package_rate_adjustment || 0);
  if (adjustment && form.adjustment_type !== 'percentage') total += adjustment * nights * rooms;
  // Length-of-stay discount (rates.packages.price_terms): fixed is per
  // room per night once the stay reaches min nights; percentage depends
  // on the room price so it is described, not summed.
  const losNights = Number(form.los_discount_min_nights || 0);
  const losValue = Number(form.los_discount_value || 0);
  if (losNights > 0 && losValue > 0 && nights >= losNights && form.los_discount_type !== 'percentage') {
    total -= losValue * nights * rooms;
  }
  (form.services || []).forEach((sv) => {
    let amount = Number(sv.service_cost || 0);
    if (!amount) return;
    if (sv.is_daily) amount *= nights;
    amount *= sv.is_per_person ? guests : rooms;
    total += amount;
  });
  return total;
};

// Package codes travel in ?package=CODE links — letters, digits, - and _ only.
const CODE_RE = /^[A-Za-z0-9_-]{1,20}$/;
const sanitizeCode = (value) => value.replace(/\s+/g, '-').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 20);

// DRF error payloads arrive as {field: ["msg"]} or {services: [{...}]};
// flatten them into one readable line instead of a JSON dump.
const describeApiError = (data, fallback = i18n.t('packages.errors.requestFailed')) => {
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.error) return data.error;
  const lines = [];
  const walk = (value, path) => {
    if (Array.isArray(value)) value.forEach((v) => walk(v, path));
    else if (value && typeof value === 'object') {
      Object.entries(value).forEach(([k, v]) => walk(v, path ? `${path}.${k}` : k));
    } else if (value !== undefined && value !== null && value !== '') {
      lines.push(path ? `${path.replace(/_/g, ' ')}: ${value}` : String(value));
    }
  };
  walk(data, '');
  return lines.length ? lines.join(' · ') : fallback;
};

const Packages = () => {
  const { t } = useTranslation();
  const [packages, setPackages] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const deleteDisclosure = useDisclosure();
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [pkgData, planData] = await Promise.all([
        packageService.getAll(),
        packageService.getRatePlans(),
      ]);
      setPackages(pkgData?.results || pkgData || []);
      setRatePlans(planData?.results || planData || []);
    } catch { setError(t('packages.errors.loadFailed')); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); onOpen(); };
  const openEdit = (pkg) => {
    setForm({
      name: pkg.name || '',
      code: pkg.code || '',
      package_type: pkg.package_type || 'other',
      description: pkg.description || '',
      base_rate_plan: pkg.base_rate_plan || '',
      package_rate_adjustment: pkg.package_rate_adjustment || '0.00',
      adjustment_type: pkg.adjustment_type || 'fixed',
      los_discount_min_nights: pkg.los_discount_min_nights || '',
      los_discount_type: pkg.los_discount_type || 'fixed',
      los_discount_value: pkg.los_discount_value || '0.00',
      start_date: pkg.start_date || '',
      end_date: pkg.end_date || '',
      is_active: pkg.is_active ?? true,
      show_in_booking_engine: pkg.show_in_booking_engine || false,
      requires_advance_booking: pkg.requires_advance_booking || false,
      advance_booking_days: pkg.advance_booking_days || '',
      promo_code: pkg.promo_code || '',
      services: (pkg.services || []).map(sv => ({
        id: sv.id,
        service_name: sv.service_name || '',
        service_type: sv.service_type || 'other',
        service_cost: sv.service_cost ?? '0.00',
        is_daily: sv.is_daily ?? true,
        is_per_person: sv.is_per_person ?? false,
      })),
    });
    setEditingId(pkg.id);
    onOpen();
  };

  // Mirrors the backend serializer so operators get a plain sentence
  // instead of a 400 payload for the common slips.
  const validateForm = () => {
    if (!form.name.trim()) return t('packages.validation.nameRequired');
    if (!CODE_RE.test(form.code.trim())) {
      return t('packages.validation.codeInvalid');
    }
    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      return t('packages.validation.endBeforeStart');
    }
    if (form.requires_advance_booking && !(Number(form.advance_booking_days) > 0)) {
      return t('packages.validation.advanceDaysRequired');
    }
    if (Number(form.los_discount_min_nights) > 0 && !(Number(form.los_discount_value) > 0)) {
      return t('packages.validation.losValueRequired');
    }
    if (form.los_discount_type === 'percentage' && Number(form.los_discount_value) >= 100) {
      return t('packages.validation.losPercentTooHigh');
    }
    if (form.adjustment_type === 'percentage' && Number(form.package_rate_adjustment) <= -100) {
      return t('packages.validation.percentTooLow');
    }
    const named = (form.services || []).filter(sv => (sv.service_name || '').trim());
    if (named.some(sv => Number(sv.service_cost) < 0)) return t('packages.validation.negativeServiceCost');
    const names = named.map(sv => sv.service_name.trim().toLowerCase());
    if (new Set(names).size !== names.length) return t('packages.validation.duplicateServiceName');
    return '';
  };

  const handleSave = async () => {
    const problem = validateForm();
    if (problem) {
      toast({ title: t('packages.toasts.checkForm'), description: problem, status: 'warning', duration: 4000 });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim(), code: form.code.trim() };
      // Only rows the operator actually named; the backend treats the
      // submitted list as the package's full service set.
      payload.services = (form.services || [])
        .filter(sv => (sv.service_name || '').trim())
        .map(sv => ({ ...sv, service_cost: sv.service_cost || '0.00' }));
      // '' -> null (not delete): PATCH must be able to CLEAR a previously
      // set rate plan / date, and the backend accepts null for all four.
      ['start_date', 'end_date', 'advance_booking_days', 'base_rate_plan',
       'los_discount_min_nights'].forEach((f) => {
        if (payload[f] === '' || payload[f] === undefined) payload[f] = null;
      });

      if (editingId) await packageService.update(editingId, payload);
      else {
        const hotelId = localStorage.getItem('admin_hotel_id');
        await packageService.create({ ...payload, hotel: hotelId ? Number(hotelId) : undefined });
      }
      toast({ title: editingId ? t('packages.toasts.updated') : t('packages.toasts.created'), status: 'success', duration: 2000 });
      onClose();
      load();
    } catch (err) {
      toast({
        title: editingId ? t('packages.toasts.saveFailed') : t('packages.toasts.createFailed'),
        description: describeApiError(err.response?.data),
        status: 'error', duration: 6000, isClosable: true,
      });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    try {
      await packageService.remove(target.id);
      toast({ title: t('packages.toasts.deleted'), status: 'success', duration: 2000 });
      deleteDisclosure.onClose();
      load();
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.code === 'package_in_use') {
        // Reservations were sold on it: the backend keeps the row so their
        // accommodation/package split survives. Retire it instead.
        try {
          await packageService.update(target.id, { is_active: false, show_in_booking_engine: false });
          toast({
            title: t('packages.toasts.deactivatedInstead'),
            description: t('packages.toasts.deactivatedInsteadDetail', { name: target.name, count: err.response.data.reservation_rooms }),
            status: 'info', duration: 7000, isClosable: true,
          });
          deleteDisclosure.onClose();
          load();
          return;
        } catch (inner) {
          toast({ title: t('packages.toasts.deactivateFailed'), description: describeApiError(inner.response?.data), status: 'error', duration: 5000 });
          return;
        }
      }
      toast({ title: t('packages.toasts.deleteFailed'), description: describeApiError(err.response?.data), status: 'error', duration: 5000 });
    }
  };

  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const setCode = (e) => setForm({ ...form, code: sanitizeCode(e.target.value) });

  const copyPackageLink = async (pkg) => {
    const text = `?package=${pkg.code}`;
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: t('packages.toasts.copied'), description: t('packages.toasts.copiedDetail', { link: text }), status: 'success', duration: 3000 });
    } catch {
      toast({ title: t('packages.toasts.copyFailed'), description: text, status: 'warning', duration: 4000 });
    }
  };

  const setService = (i, field, value) => {
    const services = form.services.map(
      (sv, idx) => (idx === i ? { ...sv, [field]: value } : sv));
    setForm({ ...form, services });
  };
  const addService = () =>
    setForm({ ...form, services: [...(form.services || []), { ...emptyService }] });
  const removeService = (i) =>
    setForm({ ...form, services: form.services.filter((_, idx) => idx !== i) });

  return (
    <PageShell title={t('packages.title')} subtitle={t('packages.count', { count: packages.length })}
      breadcrumbs={[{ label: t('packages.title') }]} loading={loading} error={error}
      actions={<Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={openCreate}>{t('packages.buttons.add')}</Button>}>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
        {packages.map(pkg => (
          <Card key={pkg.id} shadow="sm" borderWidth="1px" _hover={{ shadow: 'md' }} transition="all 0.2s">
            <CardBody>
              <HStack justify="space-between" mb={2}>
                <VStack align="start" spacing={0}>
                  <Heading size="sm">{pkg.name}</Heading>
                  <HStack spacing={1}>
                    <Text fontSize="xs" color="gray.500">{pkg.code}</Text>
                    <Badge colorScheme={TYPE_COLORS[pkg.package_type] || 'gray'} fontSize="2xs">{pkg.package_type}</Badge>
                  </HStack>
                </VStack>
                <VStack align="end" spacing={1}>
                  <Badge colorScheme={pkg.is_active ? 'green' : 'red'} fontSize="2xs">
                    {pkg.is_active ? t('packages.card.active') : t('packages.card.inactive')}
                  </Badge>
                  <Badge colorScheme={pkg.show_in_booking_engine ? 'teal' : 'gray'} variant="subtle" fontSize="2xs">
                    {pkg.show_in_booking_engine ? t('packages.card.online') : t('packages.card.staffOnly')}
                  </Badge>
                </VStack>
              </HStack>

              <Text fontSize="sm" color="gray.600" mb={2} noOfLines={2}>{pkg.description}</Text>

              {pkg.base_rate_plan_name && (
                <Text fontSize="xs" color="gray.500" mb={1}>{t('packages.card.ratePlan', { name: pkg.base_rate_plan_name })}</Text>
              )}

              {Number(pkg.package_rate_adjustment) !== 0 && (
                <HStack mb={2} spacing={2}>
                  <Text fontWeight="600" fontSize="md">
                    {pkg.adjustment_type === 'percentage' ? `${pkg.package_rate_adjustment}%` : pkg.package_rate_adjustment}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {pkg.adjustment_type === 'percentage' ? t('packages.card.percentageAdjustment')
                      : pkg.adjustment_type === 'fixed' ? t('packages.card.fixedAdjustment')
                        : t('packages.card.adjustment', { type: pkg.adjustment_type })}
                  </Text>
                </HStack>
              )}

              {(pkg.start_date || pkg.end_date) && (
                <Text fontSize="xs" color="gray.500" mb={2}>
                  {pkg.start_date || '—'} → {pkg.end_date || '—'}
                </Text>
              )}

              {pkg.promo_code && (
                <Tag size="sm" colorScheme="purple" mb={2}>{t('packages.card.promo', { code: pkg.promo_code })}</Tag>
              )}

              {pkg.show_in_booking_engine && (
                <HStack mb={2} spacing={2}>
                  <Text fontSize="xs" color="gray.500">{t('packages.card.websiteLink')}</Text>
                  <Tag size="sm" variant="outline" fontFamily="mono">?package={pkg.code}</Tag>
                  <Button size="xs" variant="ghost" onClick={() => copyPackageLink(pkg)}>{t('packages.buttons.copy')}</Button>
                </HStack>
              )}

              {pkg.services?.length > 0 && (
                <>
                  <Divider my={2} />
                  <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>{t('packages.card.includedServices')}</Text>
                  <VStack align="stretch" spacing={1}>
                    {pkg.services.map((s, i) => (
                      <HStack key={i} justify="space-between">
                        <Text fontSize="xs">{s.service_name}</Text>
                        <Tag size="sm" variant={Number(s.service_cost) ? 'subtle' : 'outline'}
                          colorScheme={Number(s.service_cost) ? 'blue' : 'green'}>
                          {servicePriceLabel(s)}
                        </Tag>
                      </HStack>
                    ))}
                  </VStack>
                </>
              )}

              <Divider my={2} />
              <HStack justify="flex-end" spacing={1}>
                <IconButton size="xs" icon={<EditIcon />} variant="ghost" onClick={() => openEdit(pkg)} aria-label={t('packages.buttons.edit')} />
                <IconButton size="xs" icon={<DeleteIcon />} variant="ghost" colorScheme="red"
                  onClick={() => { setDeleteTarget(pkg); deleteDisclosure.onOpen(); }} aria-label={t('packages.buttons.delete')} />
              </HStack>
            </CardBody>
          </Card>
        ))}
        {packages.length === 0 && !loading && (
          <Card><CardBody><Text color="gray.400" textAlign="center">{t('packages.emptyState')}</Text></CardBody></Card>
        )}
      </SimpleGrid>

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingId ? t('packages.modal.editTitle') : t('packages.buttons.add')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <HStack w="100%" spacing={4}>
                <FormControl isRequired flex={2}>
                  <FormLabel fontSize="sm">{t('packages.form.name')}</FormLabel>
                  <Input size="sm" value={form.name} onChange={set('name')} placeholder={t('packages.form.namePlaceholder')} />
                </FormControl>
                <FormControl isRequired flex={1}>
                  <FormLabel fontSize="sm">{t('packages.form.code')}</FormLabel>
                  <Input size="sm" value={form.code} onChange={setCode} placeholder="SAUNA" fontFamily="mono" />
                  <Text fontSize="xs" color="gray.500" mt={1}>{t('packages.form.codeHelp')}</Text>
                </FormControl>
              </HStack>

              <HStack w="100%" spacing={4}>
                <FormControl flex={1}>
                  <FormLabel fontSize="sm">{t('packages.form.packageType')}</FormLabel>
                  <Select size="sm" value={form.package_type} onChange={set('package_type')}>
                    {PACKAGE_TYPES.map(pt => <option key={pt.value} value={pt.value}>{t(pt.labelKey)}</option>)}
                  </Select>
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontSize="sm">{t('packages.form.baseRatePlan')}</FormLabel>
                  <Select size="sm" value={form.base_rate_plan} onChange={set('base_rate_plan')} placeholder={t('packages.form.selectPlan')}>
                    {ratePlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Select>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="sm">{t('packages.form.description')}</FormLabel>
                <Textarea size="sm" value={form.description} onChange={set('description')} rows={2} />
              </FormControl>

              <HStack w="100%" spacing={4}>
                <FormControl flex={1}>
                  <FormLabel fontSize="sm">{t('packages.form.rateAdjustment')}</FormLabel>
                  <Input size="sm" type="number" value={form.package_rate_adjustment} onChange={set('package_rate_adjustment')} />
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontSize="sm">{t('packages.form.adjustmentType')}</FormLabel>
                  <Select size="sm" value={form.adjustment_type} onChange={set('adjustment_type')}>
                    <option value="fixed">{t('packages.form.fixed')}</option>
                    <option value="percentage">{t('packages.form.percentage')}</option>
                  </Select>
                </FormControl>
              </HStack>

              {/* Length-of-stay discount on the room portion — only when
                  this package is booked (never affects room-only rates). */}
              <HStack w="100%" spacing={4} align="flex-end">
                <FormControl flex={1}>
                  <FormLabel fontSize="sm">{t('packages.form.losMinNights')}</FormLabel>
                  <Input size="sm" type="number" min="1" placeholder={t('packages.form.losMinNightsPlaceholder')}
                    value={form.los_discount_min_nights} onChange={set('los_discount_min_nights')} />
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontSize="sm">{t('packages.form.discount')}</FormLabel>
                  <Input size="sm" type="number" min="0" value={form.los_discount_value}
                    onChange={set('los_discount_value')} />
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontSize="sm">{t('packages.form.discountType')}</FormLabel>
                  <Select size="sm" value={form.los_discount_type} onChange={set('los_discount_type')}>
                    <option value="fixed">{t('packages.form.losFixed')}</option>
                    <option value="percentage">{t('packages.form.losPercentage')}</option>
                  </Select>
                </FormControl>
              </HStack>
              {Number(form.los_discount_min_nights) > 0 && (
                <Text fontSize="xs" color="gray.500" alignSelf="flex-start">
                  {form.los_discount_type === 'percentage'
                    ? t('packages.form.losHintPercentage', { nights: form.los_discount_min_nights, value: Number(form.los_discount_value || 0) })
                    : t('packages.form.losHintFixed', { nights: form.los_discount_min_nights, value: Number(form.los_discount_value || 0).toFixed(2) })}
                </Text>
              )}

              <HStack w="100%" spacing={4}>
                <FormControl flex={1}>
                  <FormLabel fontSize="sm">{t('packages.form.startDate')}</FormLabel>
                  <Input size="sm" type="date" value={form.start_date} onChange={set('start_date')} />
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontSize="sm">{t('packages.form.endDate')}</FormLabel>
                  <Input size="sm" type="date" value={form.end_date} onChange={set('end_date')} />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="sm">{t('packages.form.promoCode')}</FormLabel>
                <Input size="sm" value={form.promo_code} onChange={set('promo_code')} placeholder="SUMMER2026" />
              </FormControl>

              <Box w="100%">
                <HStack justify="space-between" mb={2}>
                  <FormLabel fontSize="sm" mb={0}>{t('packages.card.includedServices')}</FormLabel>
                  <Button size="xs" leftIcon={<AddIcon />} variant="outline" onClick={addService}>
                    {t('packages.buttons.addService')}
                  </Button>
                </HStack>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  {t('packages.form.servicesHelp')}
                </Text>
                {(form.services || []).length === 0 && (
                  <Text fontSize="xs" color="gray.400">
                    {t('packages.form.noServices')}
                  </Text>
                )}
                <VStack align="stretch" spacing={2}>
                  {(form.services || []).map((sv, i) => (
                    <Box key={sv.id || `new-${i}`} borderWidth="1px" borderRadius="md" p={2}>
                      <HStack spacing={2} mb={2}>
                        <Input size="sm" flex={2} placeholder={t('packages.form.serviceNamePlaceholder')}
                          value={sv.service_name}
                          onChange={(e) => setService(i, 'service_name', e.target.value)} />
                        <Select size="sm" flex={1} value={sv.service_type}
                          onChange={(e) => setService(i, 'service_type', e.target.value)}>
                          {SERVICE_TYPES.map(st => <option key={st.value} value={st.value}>{t(st.labelKey)}</option>)}
                        </Select>
                        <IconButton size="sm" icon={<DeleteIcon />} variant="ghost" colorScheme="red"
                          onClick={() => removeService(i)} aria-label={t('packages.buttons.removeService')} />
                      </HStack>
                      <HStack spacing={4}>
                        <FormControl flex={1}>
                          <FormLabel fontSize="xs" mb={0}>{t('packages.form.serviceCost')}</FormLabel>
                          <Input size="sm" type="number" step="0.01" min="0"
                            value={sv.service_cost}
                            onChange={(e) => setService(i, 'service_cost', e.target.value)} />
                        </FormControl>
                        <FormControl flex={1}>
                          <FormLabel fontSize="xs" mb={0}>{t('packages.form.billed')}</FormLabel>
                          <Select size="sm" value={sv.is_daily ? 'night' : 'stay'}
                            onChange={(e) => setService(i, 'is_daily', e.target.value === 'night')}>
                            <option value="stay">{t('packages.form.oncePerStay')}</option>
                            <option value="night">{t('packages.form.everyNight')}</option>
                          </Select>
                        </FormControl>
                        <FormControl flex={1}>
                          <FormLabel fontSize="xs" mb={0}>{t('packages.form.forEach')}</FormLabel>
                          <Select size="sm" value={sv.is_per_person ? 'person' : 'room'}
                            onChange={(e) => setService(i, 'is_per_person', e.target.value === 'person')}>
                            <option value="room">{t('packages.form.room')}</option>
                            <option value="person">{t('packages.form.guest')}</option>
                          </Select>
                        </FormControl>
                        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap" alignSelf="flex-end" pb={1}>
                          {servicePriceLabel(sv)}
                        </Text>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
                {(() => {
                  const example = { nights: 2, guests: 2, rooms: 1 };
                  const total = examplePackageTotal(form, example);
                  const pct = form.adjustment_type === 'percentage' && Number(form.package_rate_adjustment);
                  if (!total && !pct) return null;
                  return (
                    <Box mt={3} p={2} bg="gray.50" borderRadius="md">
                      <Text fontSize="xs" color="gray.600">
                        <Trans
                          i18nKey={pct ? 'packages.form.exampleWithPercent' : 'packages.form.example'}
                          values={{ ...example, total: total.toFixed(2), percent: Number(form.package_rate_adjustment) }}
                          components={{ b: <Text as="span" fontWeight="600" /> }}
                        />
                      </Text>
                    </Box>
                  );
                })()}
              </Box>

              <HStack w="100%" spacing={6}>
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>{t('packages.card.active')}</FormLabel>
                  <Switch isChecked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>{t('packages.form.requiresAdvanceBooking')}</FormLabel>
                  <Switch isChecked={form.requires_advance_booking} onChange={(e) => setForm({ ...form, requires_advance_booking: e.target.checked })} />
                </FormControl>
              </HStack>

              <FormControl>
                <HStack>
                  <FormLabel fontSize="sm" mb={0}>{t('packages.form.showInBookingEngine')}</FormLabel>
                  <Switch isChecked={form.show_in_booking_engine} onChange={(e) => setForm({ ...form, show_in_booking_engine: e.target.checked })} />
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {t('packages.form.bookingEngineHelp')}
                  {form.code ? ` ${t('packages.form.bookingEngineLinkHelp', { code: form.code })}` : ''}
                  {form.promo_code ? ` ${t('packages.form.bookingEnginePromoHelp')}` : ''}
                </Text>
              </FormControl>

              {form.requires_advance_booking && (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('packages.form.advanceBookingDays')}</FormLabel>
                  <Input size="sm" type="number" min="1" value={form.advance_booking_days} onChange={set('advance_booking_days')} placeholder="7" />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {t('packages.form.advanceBookingHelp')}
                  </Text>
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>{t('packages.buttons.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>{editingId ? t('packages.buttons.save') : t('packages.buttons.create')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal isOpen={deleteDisclosure.isOpen} onClose={deleteDisclosure.onClose}
        onConfirm={handleDelete} title={t('packages.deleteModal.title')} message={t('packages.deleteModal.message', { name: deleteTarget?.name })} />
    </PageShell>
  );
};

export default Packages;
